<script lang="ts">
    import { getContext, onDestroy, onMount } from 'svelte'
    import type { Writable } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import QrCreator from 'qr-creator'

    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { type SimpleFetchResult, simpleFetch } from 'hydria/services/session'

    // import HelpButton from 'hydria/components/HelpButton.svelte'

    let stoken = getContext('securityToken') as Writable<string>

    interface Props {
        hadCode: boolean
        tokenExpired: () => void
        close: () => void
        saved: () => void
    }

    let { hadCode, tokenExpired, close, saved }: Props = $props()

    let code: string | undefined = $state(undefined)
    let uri: string | undefined = $state(undefined)
    let qrElement: HTMLElement | undefined = $state(undefined)
    let qrGened: boolean = $state(false)

    let verified: boolean | 'testing' = $state(false)
    let mismatched: boolean = $state(false)
    let totp: string = $state('')
    let comment: string = ''

    let saving: boolean = $state(false)
    let isSaved: boolean = $state(false)

    async function getCode() {
        let r: SimpleFetchResult
        try {
            r = await simpleFetch('POST', '/json/security/1/totp/generate', {}, { headers: { 'x-security-token': $stoken } })
        } catch (e: any) {
            r = e
        }
        if (r && r.data && r.data.ecode === 'SEC-TOKEN') {
            tokenExpired()
            return
        }

        if (!r || !r.data || !r.data.success) {
            notify({
                type: 'danger',
                title: $_('Authentification à deux facteurs'),
                text: 'Le code n’a pas pu être chargé' + (r && r.data && r.data.error ? ` : ${r.data.error}` : '.'),
            })
            close()
            return
        }

        code = r.data.code
        uri = r.data.uri
    }
    async function validateTotp() {
        if (verified !== false) return

        verified = 'testing'
        let r: SimpleFetchResult
        try {
            r = await simpleFetch('POST', '/json/security/1/totp/test', { code: code, totp: totp }, { headers: { 'x-security-token': $stoken } })
        } catch (e: any) {
            r = e
        }
        if (r && r.data && r.data.ecode === 'SEC-TOKEN') {
            tokenExpired()
            return
        }

        if (!r || !r.data || !r.data.success) {
            verified = false
            return
        }
        if (r.data.matched) {
            verified = true
            mismatched = false
        } else {
            totp = ''
            verified = false
            mismatched = true
        }
    }

    async function save() {
        if (verified !== true || saving) return

        saving = true
        let r: SimpleFetchResult
        try {
            r = await simpleFetch('PUT', '/json/security/1/totp', { code: code, comment: comment }, { headers: { 'x-security-token': $stoken } })
        } catch (e: any) {
            r = e
        }
        if (r && r.data && r.data.ecode === 'SEC-TOKEN') {
            tokenExpired()
            return
        }

        if (!r || !r.data || !r.data.success) {
            saving = false
            return
        }
        notify({
            type: 'success',
            title: $_('Authentification à deux facteurs'),
            text: $_('Le nouveau code est maintenant en fonction.'),
        })
        saved()
        isSaved = true
    }

    function genQr() {
        if (!qrElement || !uri) return
        qrGened = true
        QrCreator.render(
            {
                text: uri,
                radius: 0,
                ecLevel: 'M',
                size: 128,
            },
            qrElement,
        )
    }

    onMount(() => {
        getCode()
    })

    $effect(() => {
        if (code && qrElement && !qrGened) genQr()
    })
    $effect(() => {
        if (qrGened && verified === false && totp.match(/^[0-9]{6}$/)) validateTotp()
    })

    let closeTimer: ReturnType<typeof setTimeout> | null = null
    $effect(() => {
        if (isSaved) {
            if (closeTimer) clearTimeout(closeTimer)
            closeTimer = setTimeout(() => {
                closeTimer = null
                close()
            }, 7000)
        }
    })

    let addedModalOpen = false
    if (!document.documentElement.classList.contains('modal-open')) {
        document.documentElement.classList.add('modal-open')
        addedModalOpen = true
    }

    onDestroy(() => {
        if (closeTimer) clearTimeout(closeTimer)
        if (addedModalOpen && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
</script>

<svelte:window
    onkeydown={(e) => {
        if (e.key === 'Escape') {
            if (!saving) {
                close()
            }
        }
    }}
/>

<div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
    <div class="modal-dialog modal-lg">
        <div class="modal-content">
            <div class="modal-header text-bg-primary d-flex justify-content-between">
                <div>{$_('Application d’authentification')}</div>
                <!-- <div><HelpButton tag="security-2fa#app" /></div> -->
            </div>
            {#if !code || !uri}
                <div class="modal-body">
                    <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                    {$_('Un moment…')}
                </div>
            {:else if isSaved}
                <div class="modal-body">
                    <p>
                        {$_('Le code secret a été sauvegardé et sera utilisé à votre prochaine connexion.')}
                    </p>
                    {#if hadCode}
                        <p>{$_('Le code précédent n’est plus actif.')}</p>
                    {/if}
                </div>
            {:else}
                <div class="modal-body">
                    <p><strong>{$_('Scanner le code QR')}</strong></p>
                    <p class="text-muted">
                        {$_('Utiliser une application d’authentification ou extension pour photographier ou scanner le code.')}
                    </p>
                    <p><span bind:this={qrElement}></span></p>
                    <p class="text-muted">
                        <span>{$_('Si vous ne pouvez pas scanner le code QR, vous pouvez entrer manuellement ce code dans l’application :')}</span>
                        <code>{code}</code>
                    </p>
                    <p><strong>{$_('Vérification du code')}</strong></p>
                    <p>
                        <input type="text" class="form-control" bind:value={totp} disabled={verified !== false} autocomplete="one-time-code" />
                    </p>
                    {#if mismatched}
                        <p class="text-danger">
                            {$_('Le code ne correspond pas. Veuillez réessayer.')}
                        </p>
                    {/if}
                </div>
                <div class="modal-footer text-bg-light">
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        disabled={saving}
                        onclick={() => {
                            close()
                        }}>{$_('Cancel')}</button
                    >
                    <button
                        type="button"
                        class="btn btn-primary"
                        onclick={() => {
                            save()
                        }}
                        disabled={verified !== true || saving}
                    >
                        {#if saving}<div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                            {$_('Saving…')}
                        {:else}{$_('Save')}
                        {/if}
                    </button>
                </div>
            {/if}
        </div>
    </div>
    <div
        class="modal-backdrop"
        onclick={() => {
            if (!saving) close()
        }}
        aria-hidden="true"
    ></div>
</div>
